import { Card, CardContent, Input, DatePicker } from '../../../components/ui';
import PhoneInput from './PhoneInput';
import TimingPicker from './TimingPicker';
import type { MicrositeType, TimingSlot } from '../types/microsite.types';

interface LocationContactSectionProps {
    micrositeType: MicrositeType;
    // Store-specific fields
    holidayInfo?: string;
    onHolidayInfoChange: (value: string) => void;
    latitude?: number;
    onLatitudeChange: (value: number) => void;
    longitude?: number;
    onLongitudeChange: (value: number) => void;
    geoLocationValidity?: Date;
    onGeoLocationValidityChange: (date: Date | null) => void;
    storePhone?: string;
    onStorePhoneChange: (value: string) => void;
    storeWhatsapp?: string;
    onStoreWhatsappChange: (value: string) => void;
    timings?: TimingSlot[];
    onTimingsChange: (timings: TimingSlot[]) => void;
    // Brand-specific fields
    brandPhone?: string;
    onBrandPhoneChange: (value: string) => void;
    brandWhatsapp?: string;
    onBrandWhatsappChange: (value: string) => void;
}

export default function LocationContactSection({
    micrositeType,
    holidayInfo,
    onHolidayInfoChange,
    latitude,
    onLatitudeChange,
    longitude,
    onLongitudeChange,
    geoLocationValidity,
    onGeoLocationValidityChange,
    storePhone,
    onStorePhoneChange,
    storeWhatsapp,
    onStoreWhatsappChange,
    timings = [],
    onTimingsChange,
    brandPhone,
    onBrandPhoneChange,
    brandWhatsapp,
    onBrandWhatsappChange,
}: LocationContactSectionProps) {
    return (
        <Card>
            <CardContent className="p-6 space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-[#e2e8f0]">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#ef4444] to-[#f87171] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">4</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-[#1e293b]">Location & Contact</h3>
                        <p className="text-sm text-[#64748b]">
                            {micrositeType === 'store'
                                ? 'Add store location, contact details, and operating hours'
                                : 'Add brand-level contact information'}
                        </p>
                    </div>
                </div>

                {micrositeType === 'store' ? (
                    <>
                        {/* Holiday Info */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-medium text-[#1e293b]">
                                    Holiday Information
                                </label>
                                <span
                                    className={`text-xs ${(holidayInfo?.length || 0) > 160 ? 'text-[#ef4444]' : 'text-[#64748b]'}`}
                                >
                                    {holidayInfo?.length || 0}/160
                                </span>
                            </div>
                            <textarea
                                value={holidayInfo || ''}
                                onChange={(e) => onHolidayInfoChange(e.target.value)}
                                placeholder="e.g., Closed on Diwali, Christmas, and New Year"
                                maxLength={160}
                                rows={2}
                                className="w-full px-4 py-2.5 bg-white border border-[#e2e8f0] rounded-lg text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent resize-none"
                            />
                            <p className="text-xs text-[#64748b]">
                                Special holiday hours or closures (max 160 characters)
                            </p>
                        </div>

                        {/* Store Location Coordinates */}
                        <div className="space-y-4">
                            <label className="block text-sm font-medium text-[#1e293b]">
                                Store Location
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <label className="block text-xs font-medium text-[#64748b]">
                                        Latitude
                                    </label>
                                    <Input
                                        type="number"
                                        step="any"
                                        value={latitude || ''}
                                        onChange={(e) => onLatitudeChange(Number(e.target.value))}
                                        placeholder="28.6139"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-xs font-medium text-[#64748b]">
                                        Longitude
                                    </label>
                                    <Input
                                        type="number"
                                        step="any"
                                        value={longitude || ''}
                                        onChange={(e) => onLongitudeChange(Number(e.target.value))}
                                        placeholder="77.2090"
                                    />
                                </div>
                                <DatePicker
                                    label="Location Validity (Optional)"
                                    value={geoLocationValidity}
                                    onChange={(date) => onGeoLocationValidityChange(date || null)}
                                    placeholder="Select validity date"
                                    minDate={new Date()}
                                />
                            </div>
                            <p className="text-xs text-[#64748b]">
                                Enter coordinates to show store location on map. Validity date is
                                optional.
                            </p>
                        </div>

                        {/* Store Contact */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <PhoneInput
                                label="Phone Number"
                                value={storePhone || ''}
                                onChange={onStorePhoneChange}
                            />
                            <PhoneInput
                                label="WhatsApp Number"
                                value={storeWhatsapp || ''}
                                onChange={onStoreWhatsappChange}
                            />
                        </div>

                        {/* Operating Hours */}
                        <div className="space-y-3">
                            <label className="block text-sm font-medium text-[#1e293b]">
                                Operating Hours
                            </label>
                            <TimingPicker timings={timings} onChange={onTimingsChange} />
                        </div>
                    </>
                ) : (
                    <>
                        {/* Brand Contact */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <PhoneInput
                                label="Brand Phone Number"
                                value={brandPhone || ''}
                                onChange={onBrandPhoneChange}
                            />
                            <PhoneInput
                                label="Brand WhatsApp Number"
                                value={brandWhatsapp || ''}
                                onChange={onBrandWhatsappChange}
                            />
                        </div>
                        <p className="text-sm text-[#64748b] bg-[#f8fafc] p-3 rounded-lg border border-[#e2e8f0]">
                            💡 These contact details will be used for all stores under this brand
                        </p>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
